// ============================================================
// HABIT OS — Bibliothek: Elektrolyte & Mineralien
// Was sind Elektrolyte · Mangelzeichen · Jeder Elektrolyt einzeln
// Nahrungsquellen · Homemade Drink · Qualitäts-Salz
// ============================================================

export const LIBRARY_ELECTROLYTES = {
  id: "electrolytes",
  label: "Elektrolyte & Mineralien",
  icon: "⚡",
  subcategories: [

    // ── GRUNDLAGEN ───────────────────────────
    {
      id: "electro_basics",
      label: "Grundlagen & Mangelzeichen",
      icon: "📚",
      habits: [
        {
          id: "hab_electro_morning",
          label: "Elektrolyte morgens als erstes",
          time: "morning",
          icon: "💧",
          why: "Elektrolyte sind am Flüssigkeitshaushalt beteiligt. Für die meisten Menschen reichen normales Trinken und eine ausgewogene Ernährung.",
          source: "Sawka et al., Medicine & Science in Sports & Exercise, 2007",
          dose: "Optional ein Elektrolyt-Getränk laut Herstellerangabe. Bei Bluthochdruck, Herz- oder Nierenerkrankungen vorher ärztlich abklären.",
        },
        {
          id: "hab_electro_signs",
          label: "Elektrolyt-Mangel erkennen & beheben",
          time: "flexible",
          icon: "🔍",
          why: "Müdigkeit, Krämpfe oder Schwindel können viele Ursachen haben. Anhaltende Beschwerden, besonders Herzrasen, ärztlich abklären lassen.",
          source: "DiNicolantonio & O'Keefe, Open Heart, 2017",
          dose: "Bei anhaltenden Beschwerden zuerst ärztlich abklären lassen.",
        },
        {
          id: "hab_electro_mineral_salt",
          label: "Salz bewusst und jodiert verwenden",
          time: "flexible",
          icon: "🧂",
          why: "Meer- und Steinsalz bestehen fast vollständig aus Natriumchlorid. Jodiertes Speisesalz ist in Deutschland eine wichtige Jodquelle.",
          source: "Functional Medicine Salt Guide",
          dose: "Sparsam salzen (DGE: höchstens 6 g Salz pro Tag) und bevorzugt jodiertes Speisesalz verwenden.",
        },
      ],
    },

    // ── NATRIUM ──────────────────────────────
    {
      id: "electro_sodium",
      label: "Natrium",
      icon: "🧂",
      habits: [
        {
          id: "hab_sodium_bone_broth",
          label: "Knochenbrühe täglich",
          time: "flexible",
          icon: "🍲",
          why: "Knochenbrühe liefert Flüssigkeit, Natrium und etwas Eiweiß.",
          source: "Rennard et al., Chest, 2000",
          dose: "1 Tasse täglich, morgens als Ersatz für Kaffee oder als Zwischenmahlzeit — selbst gekocht oder fertig gekauft",
        },
        {
          id: "hab_sodium_fermented",
          label: "Natürliche Natrium-Quellen: Fermentiertes",
          time: "flexible",
          icon: "🫙",
          why: "Sauerkraut, Miso und Oliven enthalten viel Salz. Bei Bluthochdruck die Salzmenge im Blick behalten.",
          source: "Messaoudi et al., British Journal of Nutrition, 2011",
          dose: "Täglich: 50–100 g Sauerkraut oder Kimchi + Oliven als Snack + Miso-Suppe — natürliches Natrium mit Probiotika-Bonus",
        },
      ],
    },

    // ── KALIUM ───────────────────────────────
    {
      id: "electro_potassium",
      label: "Kalium",
      icon: "🥑",
      habits: [
        {
          id: "hab_potassium_avocado",
          label: "Avocado täglich (Kalium-König)",
          time: "flexible",
          icon: "🥑",
          why: "Eine Avocado enthält mehr Kalium als eine Banane (975 mg vs 422 mg) und liefert es in einer fettreichen Matrix die die Aufnahme von fettlöslichen Vitaminen (A, D, E, K) aus dem restlichen Essen um bis zu 300% erhöht. Kalium arbeitet direkt mit Natrium zusammen um zelluläre Hydration, Muskelkontraktion und Herzrhythmus zu regulieren. Die meisten Menschen konsumieren zu viel Natrium und zu wenig Kalium.",
          source: "D'Elia et al., Journal of Hypertension, 2011",
          dose: "1/2–1 Avocado täglich: in Salaten, auf Eiern, als Guacamole — reif wenn leicht nachgibt beim Drücken",
        },
        {
          id: "hab_potassium_coconut_water",
          label: "Kokoswasser nach dem Training",
          time: "flexible",
          icon: "🥥",
          why: "Kokoswasser ist die natürlichste isotonische Flüssigkeit bekannt — mit einem Elektrolyt-Profil das dem menschlichen Blutplasma sehr ähnelt. Es enthält 600 mg Kalium, 252 mg Natrium, 60 mg Magnesium und 58 mg Kalzium pro 240 ml. Studien zeigen dass es als post-workout Hydration genauso wirksam ist wie kommerzielle Sportgetränke — ohne künstliche Zusätze und Zucker.",
          source: "Saat et al., Journal of Physiological Anthropology, 2002",
          dose: "240–480 ml nach intensivem Training oder Sauna — pure, ungesüßte Variante ohne Zusätze",
        },
        {
          id: "hab_potassium_sweet_potato",
          label: "Süßkartoffel als Kalium-Quelle",
          time: "flexible",
          icon: "🍠",
          why: "Süßkartoffeln enthalten 694 mg Kalium pro 100 g und sind gleichzeitig reich an Beta-Carotin (Vitamin A-Vorstufe), Vitamin C und Ballaststoffen. Als komplexe Kohlenhydratquelle mit niedrigem glykämischen Index stabilisieren sie den Blutzucker. Gegarte und abgekühlte Süßkartoffeln bilden resistente Stärke — hervorragendes Präbiotikum für Darm-Mikrobiom.",
          source: "USDA FoodData Central",
          dose: "3–4x pro Woche als Beilage: 150–200 g, gegrillt oder gedünstet — mit Butter oder Olivenöl für bessere Nährstoff-Absorption",
        },
      ],
    },

    // ── MAGNESIUM ────────────────────────────
    {
      id: "electro_magnesium",
      label: "Magnesium",
      icon: "🌙",
      habits: [
        {
          id: "hab_mag_dark_chocolate",
          label: "Dunkle Schokolade als Magnesium-Quelle",
          time: "flexible",
          icon: "🍫",
          why: "85%+ dunkle Schokolade enthält 64 mg Magnesium pro 30 g — und ist damit eine der magnesiumreichsten und gleichzeitig genussvollsten Quellen. Sie enthält zusätzlich Flavanole (verbessern Durchblutung und kognitive Funktion), Eisen, Zink und Theobromin (milder, länger anhaltender Stimulanzeffekt als Koffein). Die Bitterkeit signalisiert den hohen Polyphenol-Gehalt.",
          source: "Katz et al., Nutrition Reviews, 2011",
          dose: "20–30 g täglich (2–3 Riegel) mit mindestens 85% Kakaoanteil — als Dessert oder Snack, nicht als Mahlzeit-Ersatz",
        },
        {
          id: "hab_mag_pumpkin_seeds",
          label: "Kürbiskerne täglich",
          time: "flexible",
          icon: "🎃",
          why: "Kürbiskerne liefern Magnesium, Zink, Eiweiß und Ballaststoffe.",
          source: "USDA FoodData Central · Gossell-Williams et al., Journal of Medicinal Food, 2011",
          dose: "30 g täglich als Snack oder in Salaten, Joghurt oder Smoothies — roh oder leicht geröstet ohne Öl",
        },
        {
          id: "hab_mag_leafy_greens",
          label: "Dunkelgrünes Blattgemüse täglich",
          time: "flexible",
          icon: "🥬",
          why: "Spinat, Mangold, Grünkohl und andere dunkelgrüne Blätter enthalten Magnesium direkt im Chlorophyll-Molekül — Magnesium ist das Zentralatom des Chlorophylls, ähnlich wie Eisen im Hämoglobin. Das bedeutet: je dunkler das Grün, desto mehr Magnesium. Eine Tasse gekochter Spinat liefert 157 mg Magnesium — und ist gleichzeitig reich an Folat, Vitamin K und Nitrat für Durchblutung.",
          source: "USDA FoodData Central",
          dose: "2–3 Handvoll täglich: roh in Salaten oder Smoothies, gekocht als Beilage — täglich wechseln für Vielfalt",
        },
      ],
    },

    // ── KALZIUM ──────────────────────────────
    {
      id: "electro_calcium",
      label: "Kalzium",
      icon: "🦴",
      habits: [
        {
          id: "hab_calcium_sardines",
          label: "Sardinen mit Gräten (Kalzium-Powerhouse)",
          time: "flexible",
          icon: "🐟",
          why: "Sardinen mit Gräten sind die reichste natürliche Kalzium-Quelle nach Milchprodukten — mit 325 mg Kalzium pro Dose (100 g). Gleichzeitig liefern sie Omega-3, Vitamin D (essenziell für Kalzium-Aufnahme), B12, Selen und CoQ10. Das vollständige Nährstoff-Paket macht Sardinen zu einem der nährstoffdichtesten Lebensmittel überhaupt — Attia bezeichnet sie als 'perfektes Food'.",
          source: "Attia P., Outlive, 2023 · USDA FoodData Central",
          dose: "2–3 Dosen pro Woche: in Olivenöl, auf Salat oder mit Seed Crackers — immer 'with bones' kaufen",
        },
        {
          id: "hab_calcium_chia_sesame",
          label: "Chiasamen + Tahini täglich",
          time: "morning",
          icon: "🌱",
          why: "Chiasamen (179 mg Kalzium pro 30 g) und Sesam/Tahini (154 mg pro 30 g) sind außergewöhnlich kalziumreiche pflanzliche Quellen. Beide enthalten das Kalzium in pflanzlicher Form mit guter Bioverfügbarkeit wenn die Phytinsäure durch Einweichen oder Rösten reduziert wird. Tahini ist außerdem reich an Kupfer (für Bindegewebe) und Methionin — eine seltene Aminosäure in pflanzlichen Quellen.",
          source: "USDA FoodData Central",
          dose: "Täglich: 2 EL Chiasamen (eingeweicht) + 1–2 EL Tahini in Smoothies, Joghurt oder als Dressing",
        },
      ],
    },

    // ── VITAMIN C + ELEKTROLYTE ──────────────
    {
      id: "electro_vitamin_c",
      label: "Vitamin C & Elektrolyt-Synergien",
      icon: "🍊",
      habits: [
        {
          id: "hab_vitc_bell_peppers",
          label: "Rote Paprika täglich (Vitamin C Champion)",
          time: "flexible",
          icon: "🌶️",
          why: "Rote Paprika enthält dreimal mehr Vitamin C als Orangen (190 mg vs 70 mg pro 100 g) und ist gleichzeitig reich an Kalium, Kalzium und Antioxidantien. Vitamin C verbessert die Absorption von pflanzlichem Eisen aus der gleichen Mahlzeit um bis zu 300%, ist Ko-Faktor für Kollagen-Synthese und unterstützt Elektrolyt-Transport in die Zellen durch Verbesserung der Kapillarfunktion.",
          source: "Hallberg et al., American Journal of Clinical Nutrition, 1989",
          dose: "1/2 rote Paprika täglich: roh in Salaten, als Snack mit Hummus oder leicht gedünstet — roh hat mehr Vitamin C",
        },
        {
          id: "hab_vitc_kiwi",
          label: "Kiwi täglich (Vitamin C + Serotonin)",
          time: "morning",
          icon: "🥝",
          why: "Kiwi liefert viel Vitamin C. Eine kleine Studie deutet auf mögliche Effekte auf den Schlaf hin; belastbare Belege fehlen.",
          source: "Lin et al., Asia Pacific Journal of Clinical Nutrition, 2011",
          dose: "2 Kiwis täglich, morgens oder als Snack — mit Schale wenn organisch (enthält 3x mehr Ballaststoffe)",
        },
      ],
    },

    // ── HOMEMADE ELEKTROLYT-DRINK ────────────
    {
      id: "electro_recipe",
      label: "Homemade Elektrolyt-Drink",
      icon: "🥤",
      habits: [
        {
          id: "hab_electro_diy_drink",
          label: "DIY Elektrolyt-Drink",
          time: "morning",
          icon: "🍋",
          why: "Dieses einfache Rezept repliziert die Elektrolyt-Zusammensetzung von Blutplasma nahezu perfekt: Meersalz liefert Natrium + Chlorid, Zitrone/Limette liefert Kalium + Vitamin C, Honig liefert Glukose die Natrium-Transport in die Zellen aktiviert (Sodium-Glucose Cotransporter), und Kokoswasser als Option für extra Kalium + Magnesium. Weit überlegen gegenüber teuren kommerziellen Produkten mit künstlichen Farbstoffen.",
          source: "Sawka et al., Medicine & Science in Sports & Exercise, 2007",
          dose: "Rezept: 500 ml gefiltertes Wasser oder Kokoswasser · 1 Prise hochwertiges Meersalz · Saft 1/2 Zitrone oder Limette · 1–2 TL roher Honig oder Ahornsirup · optional: Prise Magnesium-Pulver, frischer Ingwer. Täglich morgens oder nach dem Sport.",
        },
        {
          id: "hab_electro_coconut_water_habit",
          label: "Kokoswasser als Basis-Hydration",
          time: "flexible",
          icon: "🥥",
          why: "Kokoswasser enthält Kalium und etwas Natrium und eignet sich nach starkem Schwitzen. Es enthält natürlichen Zucker.",
          source: "Ismail et al., Journal of the International Society of Sports Nutrition, 2007",
          dose: "240–500 ml täglich als Basis-Hydration oder Mahlzeiten-Begleiter — pur, ungezuckert, pasteurisierte Version für Alltag",
        },
      ],
    },
  ],
};